import * as React from 'react'

import { observer } from 'mobx-react'
import { Layer, Source, useMap } from 'react-map-gl/mapbox'

import {
  labelCasingPaint,
  labelHaloPaint,
  labelMeasureFont,
  labelTypographyLayout,
  markupLabelInkPaint,
  MARKUP_LABEL,
  yieldingLabelDeclutterLayout,
} from '~/client/src/shared/map/labelLayerStyle'
import { getZoomScale } from '~/client/src/shared/map/mapZoomRule'
import Markup from '~/client/src/shared/models/Markup'

import { IGroundContext } from '../MapBoxViewer/labelGroundTone'
import { measureLabelTextBox } from '../MapBoxViewer/labelTextBox'
import {
  MAX_ITEM_ZOOM_LEVEL,
  PROJECT_MARKER_ZOOM_LEVEL,
} from '../MapBoxViewer/MapBoxViewer.store'
import { TEXT_OCCLUSION_PAINT } from '../MapBoxViewer/mapOcclusionRule'
import {
  ILabelAnchor,
  LabelBoxAt,
  useLabelGroundTones,
} from '../MapBoxViewer/useLabelGroundTones'
import { useMapHoverProbe } from '../MapBoxViewer/useMapHoverProbe'
import { arrowheadChevron, chevronWingPx, dotRadiusPx } from './markupArrowhead'
import { DEFAULT_MARKUP_STYLE, splitHexAlpha } from './MarkupDrawController'
import { MarkupEditorTextChannel } from './markupEditorTextChannel'
import {
  IMarkupLabelPins,
  pinScreenBoxes,
  screenBoxesMeet,
} from './markupLabelPins'
import MarkupTextLayer, { useTextMarkupItems } from './MarkupTextLayer'
import { TEXT_SCALE_REF_ZOOM, textGroundPerPx } from './markupTextScale'
import { inputOfMarkup } from './markupTextStyle'
import { ArrowheadType } from './markupTools'
import { textMarkupLabelSpots } from './textMarkupLabelAnchor'
import { ITextLayout } from './textMarkupLayout'
import { textMarkupEntry } from './textMarkupRegistry'

// Opacity multiplier for the dimmed (reference) variant — other workflows'
// markups read as faded context next to the current workflow's solid ones.
const MUTED_OPACITY = 0.35

// Labels must stay legible — kept far more opaque than the dimmed geometry.
const MUTED_LABEL_OPACITY = 0.65

// Annotations, one tier below the workflow-pin identity badge: smaller, lighter and
// in muted ink so pin labels stay the dominant read on the map.
const LABEL_TEXT_SIZE = MARKUP_LABEL.sizePx
// mapbox's own default wrap width, which this layer never overrides.
const MARKUP_MAX_WIDTH_PX = 10 * MARKUP_LABEL.sizePx
const MARKUP_MEASURE_FONT = labelMeasureFont(MARKUP_LABEL)
const LABEL_OFFSET_MARGIN_PX = 4

// Keeps a popup tip off the glyph edge rather than exactly on it.
const POPUP_TIP_GAP_PX = 3
// Screen height of the GL label above its map anchor: one line box, both halo edges
// and the gap. A popup anchored at the same coord uses this as its offset so its tip
// sits just above the label instead of overlapping it.
export const MARKUP_LABEL_HEIGHT_PX =
  MARKUP_LABEL.lineHeightPx + 2 * MARKUP_LABEL.haloWidthPx + POPUP_TIP_GAP_PX

interface IProps {
  markups: Markup[]
  visible?: boolean
  // So two instances (current + reference) can coexist on one map without id collisions.
  idPrefix?: string
  muted?: boolean
  // Stable Map ref (not a per-render closure) so the layer skips re-render during camera moves.
  labels?: Map<string, string>
  showLabels?: boolean
  activeWorkflowIds?: Set<string>
  // The copy-select source: highlighted geometry, or a ringed text. The editor hit-tests both.
  selectedId?: string | null
  // Label tone used until (or when) the surface can't be sampled — driven by the
  // basemap: satellite = 'dark', street = 'light'.
  // What each label stands on, composited from the scene by the host.
  ground: IGroundContext
  // Honors the "Allow labels to overlap" Map Setting (like CollisionLayer): off
  // lets Mapbox declutter colliding labels instead of piling them at low zoom.
  allowOverlap?: boolean
  // The map view's "scale with zoom" setting. A markup label is chrome naming an annotation, and
  // it now shrinks with the camera like every other label — see labelTypographyLayout.
  isZoomScalingEnabled?: boolean
  // Keyed by the label layer id: several instances share one map.
  onHoverChange?: (hovered: boolean, source: string) => void
  interactive?: boolean
  // The session mount: while an editor is attached, its text comes from the editor and the
  // editor draws the geometry itself, so only the text layer stays.
  editorText?: MarkupEditorTextChannel
  // Text markups drawn this many times their size (the export's Markup text).
  textSizeScale?: number
  // A text markup's label stands where it keeps clear of these pins, when it can.
  pins?: IMarkupLabelPins
  // Changes when the ground under the labels changes with neither the camera nor a source saying
  // so (an export hiding a map file), so every label is read again.
  labelRerunKey?: string
}

function toLngLat(markup: Markup): number[][] {
  return (markup.coordinates ?? []).map(c => [c.longitude, c.latitude])
}

// Mapbox's data-driven `['get', 'color']` can't parse an 8-digit (alpha) hex at
// runtime (renders black), so split transparency into a 6-digit color + an
// explicit opacity that the paint multiplies.
function strokeOf(markup: Markup): { color: string; opacity: number } {
  const { color, opacity } = splitHexAlpha(
    markup.color ?? DEFAULT_MARKUP_STYLE.color,
  )
  return { color, opacity: (markup.opacity ?? 1) * opacity }
}

function toFeature(markup: Markup): GeoJSON.Feature {
  const stroke = strokeOf(markup)
  const fill = splitHexAlpha(
    markup.fillColor ?? markup.color ?? DEFAULT_MARKUP_STYLE.color,
  )
  const properties = {
    markupId: markup.id,
    color: stroke.color,
    strokeWidth: markup.strokeWidth ?? DEFAULT_MARKUP_STYLE.strokeWidth,
    opacity: stroke.opacity,
    strokeStyle: markup.strokeStyle ?? 'solid',
    fillColor: fill.color,
    fillOpacity: fill.opacity,
  }
  const points = toLngLat(markup)

  if (markup.geometryType === 'Polygon') {
    const first = points[0]
    const last = points[points.length - 1]
    const ring =
      points.length > 0 && (first[0] !== last[0] || first[1] !== last[1])
        ? [...points, first]
        : points
    return {
      type: 'Feature',
      properties,
      geometry: { type: 'Polygon', coordinates: [ring] },
    }
  }

  return {
    type: 'Feature',
    properties,
    geometry: { type: 'LineString', coordinates: points },
  }
}

// Legacy markups (saved before per-end caps) only have lineType: an 'arrow'
// drew a head at the end. Mirror that when the cap fields are absent.
function arrowheads(markup: Markup): {
  start: ArrowheadType
  end: ArrowheadType
} {
  const fallbackEnd = markup.lineType === 'arrow' ? 'arrow' : 'none'
  return {
    start: (markup.arrowheadStart as ArrowheadType) ?? 'none',
    end: (markup.arrowheadEnd as ArrowheadType) ?? fallbackEnd,
  }
}

// Mutates the passed line/dot arrays so a single pass feeds both render layers. Only
// zoom is needed to size the (geo) caps — they tilt/pan with the map on their own — so
// no screen projection is threaded through.
interface Projection {
  zoom: number
}

function pushArrowheads(
  markup: Markup,
  lines: GeoJSON.Feature[],
  dots: GeoJSON.Feature[],
  projection?: Projection,
): void {
  const points = toLngLat(markup)
  if (points.length < 2) return
  const { start, end } = arrowheads(markup)
  const { color, opacity } = strokeOf(markup)
  const strokeWidth = markup.strokeWidth ?? DEFAULT_MARKUP_STYLE.strokeWidth
  const cap = (type: ArrowheadType, prev: number[], tip: number[]): void => {
    if (type === 'arrow') {
      // Skip until the map is ready (zoom unknown); fills in on the next render.
      if (!projection) return
      const chevron = arrowheadChevron(
        prev,
        tip,
        chevronWingPx(strokeWidth, prev, tip, projection.zoom),
        projection.zoom,
      )
      if (chevron) {
        lines.push({
          type: 'Feature',
          properties: { color, strokeWidth, opacity },
          geometry: { type: 'LineString', coordinates: chevron },
        })
      }
    } else if (type === 'circle') {
      if (!projection) return
      const radius = dotRadiusPx(strokeWidth, prev, tip, projection.zoom)
      if (radius > 0) {
        dots.push({
          type: 'Feature',
          properties: { color, radius, opacity },
          geometry: { type: 'Point', coordinates: tip },
        })
      }
    }
  }
  cap(start, points[1], points[0])
  cap(end, points[points.length - 2], points[points.length - 1])
}

// One pass over the line markups builds both the line/chevron collection and the
// arrowhead-dot collection (each <Source> consumes one), instead of running the
// per-markup cap geometry twice.
function lineAndArrowheadDots(
  markups: Markup[],
  projection?: Projection,
): { lines: GeoJSON.FeatureCollection; dots: GeoJSON.FeatureCollection } {
  const lineFeatures: GeoJSON.Feature[] = []
  const dotFeatures: GeoJSON.Feature[] = []
  markups.forEach(markup => {
    lineFeatures.push(toFeature(markup))
    pushArrowheads(markup, lineFeatures, dotFeatures, projection)
  })
  return {
    lines: { type: 'FeatureCollection', features: lineFeatures },
    dots: { type: 'FeatureCollection', features: dotFeatures },
  }
}

function collection(markups: Markup[]): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: markups.map(toFeature) }
}

const EMPTY_FC: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
}

// Label lift off its 'bottom' anchor, straight up, in ems of LABEL_TEXT_SIZE. A SCALAR by
// design: an array property would be JSON-stringified in the queryRenderedFeatures path (the
// label hover probe), making a ['get'] 2-vector fail its array<number,2> assertion on every
// hover hit-test. Text markups clear their box by where the label stands instead (below).
const LABEL_LIFT_EMS = LABEL_OFFSET_MARGIN_PX / LABEL_TEXT_SIZE

// Screen px from a label's point up to a popup tip just above the label.
export const MARKUP_LABEL_POPUP_OFFSET_PX =
  LABEL_OFFSET_MARGIN_PX + MARKUP_LABEL_HEIGHT_PX

// The geo point a markup's label stands on: its first vertex, or for a measured text markup
// the first spot above its box that isClear accepts, else the first.
function markupLabelPoint(
  markup: Markup,
  layout: ITextLayout | undefined,
  groundPerPx: number,
  bearing: number,
  isClear: (point: { longitude: number; latitude: number }) => boolean = () =>
    true,
): { longitude: number; latitude: number } {
  const { longitude, latitude } = markup.coordinates[0]
  if (!layout) return { longitude, latitude }
  const spots = textMarkupLabelSpots(
    longitude,
    latitude,
    layout,
    markup.rotation ?? 0,
    groundPerPx,
    bearing,
  )
  return spots.find(isClear) ?? spots[0]
}

/**
 * Read-only render of saved markups, all in GL: geometry (shapes/lines/arrows) as style
 * layers, text markups as one custom layer of ground-plane quads (MarkupTextLayer).
 * Mounted inside the react-map-gl <Map>.
 */
const MarkupLayer: React.FC<IProps> = ({
  markups,
  visible = true,
  idPrefix = 'markup-display',
  muted = false,
  labels,
  showLabels = true,
  activeWorkflowIds,
  selectedId,
  ground,
  allowOverlap = true,
  isZoomScalingEnabled,
  onHoverChange,
  interactive = true,
  editorText,
  textSizeScale = 1,
  pins,
  labelRerunKey,
}) => {
  const { current: map } = useMap()
  const showGeometry = !editorText?.isAttached
  // Re-render on zoom to re-fit the zoom-driven cap size, and after a rotation to re-place
  // the text labels (geo geometry reprojects on pan/tilt by itself).
  const [, bumpOnZoom] = React.useState(0)
  React.useEffect(() => {
    if (!map) return undefined
    const bump = (): void => bumpOnZoom(t => t + 1)
    // rotateend, not continuous 'rotate' — re-creating the sources per frame froze the map.
    map.on('zoom', bump)
    map.on('rotateend', bump)
    return () => {
      map.off('zoom', bump)
      map.off('rotateend', bump)
    }
  }, [map])

  // Text labels are placed for the camera at rest: they follow a zoom or rotation when the
  // gesture ends, not on every frame of it (a touch pinch changes both).
  const [rest, setRest] = React.useState(() => ({
    zoom: map?.getZoom() ?? TEXT_SCALE_REF_ZOOM,
    bearing: map?.getBearing() ?? 0,
  }))
  React.useEffect(() => {
    if (!map) return undefined
    const update = (): void => {
      const zoom = map.getZoom()
      const bearing = map.getBearing()
      setRest(prev =>
        prev.zoom === zoom && prev.bearing === bearing
          ? prev
          : { zoom, bearing },
      )
    }
    update()
    map.on('zoomend', update)
    map.on('rotateend', update)
    return () => {
      map.off('zoomend', update)
      map.off('rotateend', update)
    }
  }, [map])
  const groundPerPx = textGroundPerPx(rest.zoom)

  // Pointer cursor over the clickable label layer, and which label it is on: the probe
  // already ran for the cursor, so recording the hit is what turns it blue too.
  const hasClickableLabels = !!(visible && showLabels && labels?.size)
  const [hoveredMarkupId, setHoveredMarkupId] = React.useState<string>(null)
  const labelLayerId = `${idPrefix}-labels`
  useMapHoverProbe(map, {
    enabled: hasClickableLabels && interactive,
    layerIds: [labelLayerId],
    throttleMs: 120,
    onHit: hit => {
      setHoveredMarkupId((hit?.properties?.markupId as string) ?? null)
      onHoverChange?.(!!hit, labelLayerId)
    },
  })

  const zoom = map ? map.getZoom() : undefined
  // The label's own ramp, the shared camera one every label rides. The lift is re-expressed
  // in the label's ems so the painted gap survives the label shrinking.
  const labelScale =
    isZoomScalingEnabled && zoom != null ? getZoomScale(zoom) : 1

  const texts = React.useMemo(
    () => markups.filter(m => m.kind === 'text' && m.coordinates?.length > 0),
    [markups],
  )
  const markupInputs = React.useMemo(() => texts.map(inputOfMarkup), [texts])
  const textItems = useTextMarkupItems(editorText?.inputs ?? markupInputs)

  const markupsById = React.useMemo(
    () => new Map(markups.map(m => [m.id, m])),
    [markups],
  )
  // Anchored 'bottom' and lifted straight up, so the glyphs sit ABOVE the markup's corner —
  // the opposite direction to a pin label. Negative because the offset is measured downward.
  const boxAt = React.useCallback<LabelBoxAt>(
    (anchor, z) => {
      const markup = markupsById.get(anchor.id)
      const measured = measureLabelTextBox(
        (markup && labels?.get(markup.sitePermitId)) ?? '',
        MARKUP_MEASURE_FONT,
        MARKUP_MAX_WIDTH_PX,
        MARKUP_LABEL.sizePx,
      )
      // Measured at the tier size, then scaled: the cells have to cover the glyphs as DRAWN,
      // or the ground read answers for a label larger than the one on screen.
      const scale = isZoomScalingEnabled ? getZoomScale(z) : 1
      const lineHeight = MARKUP_LABEL.lineHeightPx * scale
      const height = measured.lines * lineHeight
      return {
        dx: 0,
        dy:
          -(LABEL_LIFT_EMS * LABEL_TEXT_SIZE + lineHeight / 2) -
          (height - lineHeight) / 2,
        width: measured.width * scale,
        height,
      }
    },
    [markupsById, labels, isZoomScalingEnabled],
  )

  // textItems: a text's label moves above its box once the box is measured. Kept clear of the
  // pins as they stand when a zoom ends: pins hold their screen size while the box scales.
  const pinZoom = pins?.points.length ? rest.zoom : null
  const textLayouts = React.useMemo(
    () =>
      new Map(
        textItems.map(item => [item.id, textMarkupEntry(item.specId).layout]),
      ),
    [textItems],
  )
  const labelPoints = React.useMemo(() => {
    const points = new Map<string, { longitude: number; latitude: number }>()
    if (!labels || !showLabels) return points
    const project = (lngLat: [number, number]) => map.project(lngLat)
    const pinBoxes =
      map && pinZoom != null ? pinScreenBoxes(pins, pinZoom, project) : []
    markups.forEach(markup => {
      if (!markup.coordinates?.[0] || !labels.get(markup.sitePermitId)) return
      const isClear = (point: { longitude: number; latitude: number }) => {
        const at = project([point.longitude, point.latitude])
        const box = boxAt({ id: markup.id, ...point }, pinZoom)
        const label = {
          x: at.x + box.dx - box.width / 2,
          y: at.y + box.dy - box.height / 2,
          width: box.width,
          height: box.height,
        }
        return !pinBoxes.some(pin => screenBoxesMeet(label, pin))
      }
      points.set(
        markup.id,
        markupLabelPoint(
          markup,
          textLayouts.get(markup.id),
          groundPerPx,
          rest.bearing,
          pinBoxes.length ? isClear : undefined,
        ),
      )
    })
    return points
  }, [
    map,
    markups,
    labels,
    showLabels,
    textLayouts,
    groundPerPx,
    rest.bearing,
    pinZoom,
    pins,
    boxAt,
  ])

  const labelAnchors = React.useMemo<ILabelAnchor[]>(
    () => [...labelPoints].map(([id, point]) => ({ id, ...point })),
    [labelPoints],
  )
  const renderedLabels = React.useMemo(
    () => ({ layerId: `${idPrefix}-labels`, idProperty: 'markupId' }),
    [idPrefix],
  )
  const tones = useLabelGroundTones(
    labelAnchors,
    ground,
    boxAt,
    renderedLabels,
    visible && showLabels,
    labelRerunKey,
  )

  // Memoized so a parent re-render during pan/tilt/rotate (markups, zoom, bearing
  // unchanged) returns the same refs — <Source> skips setData, Markers skip reconcile.
  const polygons = React.useMemo(
    () => collection(markups.filter(m => m.geometryType === 'Polygon')),
    [markups],
  )
  const lineMarkups = React.useMemo(
    () => markups.filter(m => m.geometryType === 'LineString'),
    [markups],
  )
  // zoom drives the geo cap size → recompute on zoom only, not on pan/tilt.
  const { lines, dots } = React.useMemo(
    () =>
      lineAndArrowheadDots(lineMarkups, zoom == null ? undefined : { zoom }),
    [lineMarkups, zoom],
  )
  // Labels render as a single GL symbol layer (GPU) — one Point per labelled markup —
  // instead of one HTML <Marker> each, which mapbox repositions per camera frame.
  const labelFeatures = React.useMemo<GeoJSON.FeatureCollection>(() => {
    if (!labels || !showLabels) return EMPTY_FC
    const features: GeoJSON.Feature[] = []
    markups.forEach(markup => {
      const point = labelPoints.get(markup.id)
      if (!point) return
      const label = labels.get(markup.sitePermitId)
      const selected = !!activeWorkflowIds?.has(markup.sitePermitId)
      // Ems of the RENDERED size, which is now smaller than the tier's. Dividing by the ramp
      // holds the painted clearance exactly where it was.
      const lift = LABEL_LIFT_EMS / labelScale
      features.push({
        type: 'Feature',
        properties: {
          markupId: markup.id,
          label,
          selected,
          // `hovered` is deliberately absent: it lives in feature state, so hovering does not
          // rebuild and re-upload this collection. `selected` stays a property — it feeds
          // sortKey below, and symbol-sort-key is layout, which cannot read state.
          tone: tones.get(markup.id)?.tone ?? ground.basemap,
          lift,
          // Lower = placed first → the selected label survives declutter.
          sortKey: selected ? 0 : 1,
        },
        geometry: {
          type: 'Point',
          coordinates: [point.longitude, point.latitude],
        },
      })
    })
    return { type: 'FeatureCollection', features }
  }, [
    markups,
    labels,
    showLabels,
    activeWorkflowIds,
    labelPoints,
    labelScale,
    tones,
    ground,
  ])

  // Two writes per hover change against a collection that no longer changes. Guarded on the
  // source rather than on isStyleLoaded: the source is what setFeatureState addresses, and a
  // style reload remounts it while the style still reports loaded.
  React.useEffect(() => {
    if (!hoveredMarkupId) return
    const gl = map?.getMap()
    const source = `${idPrefix}-labels-source`
    if (!gl?.getSource(source)) return
    const feature = { source, id: hoveredMarkupId }
    gl.setFeatureState(feature, { hovered: true })
    return () => {
      if (!gl.getSource(source)) return
      gl.setFeatureState(feature, { hovered: false })
    }
  }, [map, idPrefix, hoveredMarkupId])

  // Not gated on markups.length: react-map-gl appends with no beforeId, so a subtree that
  // mounts once the data arrives lands on top of the pins that mounted before it — wrong for
  // paint (a pin is what you click) and wrong for collision, which places topmost-first and
  // would let a markup label outrank every pin label. Every source memo above is a filter over
  // `markups`, so mounting with none costs empty layers and keeps JSX order authoritative.
  if (!visible) return null

  const op = (expr: unknown): unknown =>
    muted ? ['*', expr, MUTED_OPACITY] : expr
  const selectFilter = ['==', ['get', 'markupId'], selectedId ?? '__none__']

  const textLayer = (
    <MarkupTextLayer
      idPrefix={idPrefix}
      items={textItems}
      opacity={muted ? MUTED_OPACITY : 1}
      highlightId={selectedId}
      sizeScale={textSizeScale}
    />
  )
  // The text layer keeps its place in the tree either way: remounting it would drop the GL layer
  // for a frame when an editor attaches or detaches.
  return (
    <>
      {showGeometry && (
        <>
          <Source id={`${idPrefix}-polygons`} type="geojson" data={polygons}>
            <Layer
              id={`${idPrefix}-fill`}
              type="fill"
              paint={{
                'fill-color': ['get', 'fillColor'],
                'fill-opacity': op(['get', 'fillOpacity']) as never,
              }}
            />
            <Layer
              id={`${idPrefix}-fill-outline`}
              type="line"
              filter={['!=', ['get', 'strokeStyle'], 'dashed']}
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{
                'line-color': ['get', 'color'],
                'line-width': ['get', 'strokeWidth'],
                'line-opacity': op(['get', 'opacity']) as never,
              }}
            />
            <Layer
              id={`${idPrefix}-fill-outline-dashed`}
              type="line"
              filter={['==', ['get', 'strokeStyle'], 'dashed']}
              layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
              paint={{
                'line-color': ['get', 'color'],
                'line-width': ['get', 'strokeWidth'],
                'line-opacity': op(['get', 'opacity']) as never,
                'line-dasharray': [2, 2],
              }}
            />
            {selectedId != null && (
              <Layer
                id={`${idPrefix}-poly-highlight`}
                type="line"
                filter={selectFilter as never}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': ['get', 'color'],
                  'line-width': ['+', ['get', 'strokeWidth'], 6],
                  'line-opacity': 0.4,
                }}
              />
            )}
          </Source>
          <Source id={`${idPrefix}-lines`} type="geojson" data={lines}>
            {selectedId != null && (
              <Layer
                id={`${idPrefix}-line-highlight`}
                type="line"
                filter={selectFilter as never}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': ['get', 'color'],
                  'line-width': ['+', ['get', 'strokeWidth'], 6],
                  'line-opacity': 0.4,
                }}
              />
            )}
            <Layer
              id={`${idPrefix}-line`}
              type="line"
              filter={['!=', ['get', 'strokeStyle'], 'dashed']}
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{
                'line-color': ['get', 'color'],
                'line-width': ['get', 'strokeWidth'],
                'line-opacity': op(['get', 'opacity']) as never,
              }}
            />
            <Layer
              id={`${idPrefix}-line-dashed`}
              type="line"
              filter={['==', ['get', 'strokeStyle'], 'dashed']}
              layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
              paint={{
                'line-color': ['get', 'color'],
                'line-width': ['get', 'strokeWidth'],
                'line-opacity': op(['get', 'opacity']) as never,
                'line-dasharray': [2, 2],
              }}
            />
          </Source>
          <Source id={`${idPrefix}-arrowdots`} type="geojson" data={dots}>
            <Layer
              id={`${idPrefix}-arrowdot`}
              type="circle"
              paint={{
                'circle-color': ['get', 'color'],
                'circle-radius': ['get', 'radius'],
                'circle-opacity': op(['get', 'opacity']) as never,
              }}
            />
          </Source>
          <Source
            id={`${idPrefix}-labels-source`}
            type="geojson"
            data={labelFeatures}
            // Feature state binds by feature id, and these features have none of their own.
            // markupId is unique per markup, which is what the hover probe reports.
            promoteId="markupId"
          >
            <Layer
              id={`${idPrefix}-labels`}
              type="symbol"
              minzoom={PROJECT_MARKER_ZOOM_LEVEL}
              maxzoom={MAX_ITEM_ZOOM_LEVEL}
              layout={
                {
                  'text-field': ['get', 'label'],
                  ...labelTypographyLayout(MARKUP_LABEL, {
                    isZoomScalingEnabled,
                  }),
                  'text-anchor': 'bottom',
                  // Radial (scalar) — off a 'bottom' anchor it lifts straight up.
                  'text-radial-offset': ['get', 'lift'] as never,
                  ...yieldingLabelDeclutterLayout(allowOverlap, {
                    isZoomScalingEnabled,
                  }),
                } as never
              }
              paint={
                {
                  'text-color': markupLabelInkPaint(),
                  'text-halo-color': labelHaloPaint(),
                  ...labelCasingPaint(MARKUP_LABEL, { isZoomScalingEnabled }),
                  'text-opacity': muted ? MUTED_LABEL_OPACITY : 1,
                  // Text only. The geometry layers below stay unoccluded on purpose: they all drive
                  // line-opacity per feature, and mapbox does not support line-occlusion-opacity
                  // alongside a data-driven line-opacity.
                  ...TEXT_OCCLUSION_PAINT,
                } as never
              }
            />
          </Source>
        </>
      )}
      {textLayer}
    </>
  )
}

export default observer(MarkupLayer)
